(function () {
  'use strict';

  /**
   * Dieux GA4 event tracking.
   *
   * All theme events flow through sendToGA4 for consent checks, de-dupe,
   * param cleanup, and debug logging.
   *
   * Markup hooks: data-click-*, data-ga4-click, data-ga4-hero, and
   * .js-add-to-cart*.
   * CustomEvents: upsell:added, cross-sell:added, and search:*.
   */

  const GA4_MEASUREMENT_ID = 'G-PTVTTX2R7P';
  const DEBUG = false;
  const DEDUPE_WINDOW_MS = 2000;

  function isAnalyticsAllowed() {
    try {
      if (window.Shopify && window.Shopify.customerPrivacy) {
        if (typeof Shopify.customerPrivacy.analyticsProcessingAllowed === 'function') {
          return Shopify.customerPrivacy.analyticsProcessingAllowed();
        }
        if (typeof Shopify.customerPrivacy.currentVisitorConsent === 'function') {
          const c = Shopify.customerPrivacy.currentVisitorConsent();
          if (c && c.analytics !== undefined) {
            return c.analytics === 'yes' || c.analytics === true;
          }
        }
      }
    } catch (e) {}
    return true;
  }

  // Piggyback on the Google & YouTube app tag so custom events share its
  // consent, session, client_id, and attribution context. Do not load or
  // configure gtag here.
  window.dataLayer = window.dataLayer || [];

  const QUEUE_POLL_MS = 100;
  const QUEUE_MAX_WAIT_MS = 10000;
  const eventQueue = [];
  let gtagMounted = false;
  let pollHandle = null;
  let pollDeadline = 0;

  function isConsentResolved() {
    return !!(window.Shopify && window.Shopify.customerPrivacy &&
              typeof window.Shopify.customerPrivacy.analyticsProcessingAllowed === 'function');
  }

  function isGtagConfigured() {
    if (gtagMounted) return true;
    const dl = window.dataLayer;
    if (!dl || !dl.length) return false;
    for (let i = 0; i < dl.length; i++) {
      const e = dl[i];
      if (!e) continue;
      const cmd = e[0] || e['0'];
      const id  = e[1] || e['1'];
      if (cmd === 'config' && id === GA4_MEASUREMENT_ID) {
        gtagMounted = true;
        return true;
      }
    }
    return false;
  }

  function isReady() {
    return isConsentResolved() && isGtagConfigured();
  }

  // gtag.js ignores plain arrays; queued events must flush as Arguments-shaped
  // objects with numeric keys.
  function pushAsGtagArguments(argsArray) {
    (function () { window.dataLayer.push(arguments); }).apply(null, argsArray);
  }

  function flushQueue() {
    while (eventQueue.length) {
      const args = eventQueue.shift();
      pushAsGtagArguments(args);
      if (DEBUG) console.log('[GA4] ⤴ flushed queued event:', args[1]);
    }
  }

  function startPolling() {
    if (pollHandle) return;
    pollDeadline = Date.now() + QUEUE_MAX_WAIT_MS;
    pollHandle = setInterval(function () {
      if (isReady()) {
        clearInterval(pollHandle);
        pollHandle = null;
        if (DEBUG) console.log('[GA4] gtag mounted + consent resolved; flushing ' + eventQueue.length + ' queued event(s)');
        flushQueue();
      } else if (Date.now() >= pollDeadline) {
        clearInterval(pollHandle);
        pollHandle = null;
        if (DEBUG) console.warn('[GA4] gtag/consent never became ready in ' + QUEUE_MAX_WAIT_MS + 'ms; dropping ' + eventQueue.length + ' queued event(s)');
        eventQueue.length = 0;
      }
    }, QUEUE_POLL_MS);
  }

  window.ga4Event = function () {
    if (isReady()) {
      window.dataLayer.push(arguments);
    } else {
      const a = new Array(arguments.length);
      for (let i = 0; i < arguments.length; i++) a[i] = arguments[i];
      eventQueue.push(a);
      if (DEBUG) console.log('[GA4] ⏸ queued (gtag not mounted yet):', a[1]);
      startPolling();
    }
  };

  const recentEvents = new Map();

  function getEventKey(eventName, params) {
    return [
      eventName,
      params.custom_click_name || '',
      params.custom_button_text || '',
      params.custom_element_type || '',
      params.custom_click_id || '',
      params.custom_element_id || '',
      params.page_path || '',
      params.custom_product_handle || '',
      params.custom_variant_id || '',
      params.productVariantId || '',
      params.productTitle || ''
    ].join('|');
  }

  function isDuplicateEvent(eventName, params) {
    const key = getEventKey(eventName, params);
    const now = Date.now();
    for (const [k, t] of recentEvents.entries()) {
      if (now - t > DEDUPE_WINDOW_MS) recentEvents.delete(k);
    }
    if (recentEvents.has(key) && (now - recentEvents.get(key)) < DEDUPE_WINDOW_MS) {
      return true;
    }
    recentEvents.set(key, now);
    return false;
  }

  function cleanParams(params) {
    const clean = {};
    for (const [k, v] of Object.entries(params || {})) {
      if (v === null || v === undefined) continue;
      const key = String(k).replace(/[^a-zA-Z0-9_]/g, '_').substring(0, 40);
      if (typeof v === 'string') {
        if (k === 'custom_button_text') {
          const c = cleanText(v);
          if (c !== null) clean[key] = c;
        } else {
          const t = v.trim();
          if (t.length > 0) clean[key] = t.substring(0, 100);
        }
      } else if (typeof v === 'number' || typeof v === 'boolean') {
        clean[key] = String(v);
      }
    }
    return clean;
  }

  function sendToGA4(eventName, params) {
    if (!isAnalyticsAllowed()) {
      if (DEBUG) console.log('[GA4] Dropped (no consent):', eventName);
      return;
    }

    if (typeof window.ga4Event !== 'function') {
      if (DEBUG) console.error('[GA4] ga4Event not ready:', eventName);
      return;
    }

    const clean = cleanParams(params);
    if (isDuplicateEvent(eventName, clean)) return;

    // Keep supplemental theme events out of Ads and Merchant Center.
    clean.send_to = GA4_MEASUREMENT_ID;

    window.ga4Event('event', eventName, clean);
    if (DEBUG) console.log('[GA4] ✓ Event sent:', eventName, clean);
  }

  function getDataAttribute(el, attrName) {
    if (!el) return null;
    const camel = attrName.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
    let n = el;
    while (n && n !== document.body) {
      if (n.dataset && n.dataset[camel]) return n.dataset[camel];
      const v = n.getAttribute && n.getAttribute('data-' + attrName);
      if (v !== null && v !== undefined && v !== '') return v;
      n = n.parentElement;
    }
    return null;
  }

  // Wrapper labels can override generic inner block labels.
  const getClickName = (el) =>
    getDataAttribute(el, 'click-widget') || getDataAttribute(el, 'click-name');
  const getClickId        = (el) => getDataAttribute(el, 'click-id');
  const getClickLinkName  = (el) => getDataAttribute(el, 'click-link-name');
  const getClickProduct   = (el) => getDataAttribute(el, 'click-product');
  const getClickVariant   = (el) => getDataAttribute(el, 'click-variant');
  const getClickProductId = (el) => getDataAttribute(el, 'click-productid');
  const getClickVariantId = (el) => getDataAttribute(el, 'click-variantid');

  function getElementId(el) {
    if (!el) return null;
    if (el.id) return el.id;
    let n = el.parentElement;
    while (n && n !== document.body) {
      if (n.id) return n.id;
      n = n.parentElement;
    }
    return null;
  }

  function getProductHandle(url) {
    if (!url) return null;
    const m = url.match(/\/products\/([^?#/]+)/);
    return m ? m[1] : null;
  }

  function getVariantIdFromUrl(url) {
    if (!url) return null;
    try { return new URL(url, window.location.origin).searchParams.get('variant'); }
    catch (e) { return null; }
  }

  function getSectionInfo(el) {
    const s = el.closest && el.closest('.shopify-section');
    if (!s || !s.id) return { sectionId: null, sectionHandle: null };
    let id = s.id.replace(/^shopify-section-/, '').replace(/^template--\d+__/, '');
    const m = id.match(/__([^_]+)$/);
    return { sectionId: id, sectionHandle: m ? m[1] : id };
  }

  function cleanText(text) {
    if (!text) return null;
    const div = document.createElement('div');
    div.innerHTML = text;
    div.querySelectorAll('script, style').forEach((el) => el.remove());
    let c = (div.textContent || div.innerText || '')
      .replace(/[\r\n\t\f\v]+/g, ' ')
      .replace(/ /g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (c.includes('{') || c.match(/^\s*--[\w-]+\s*:/)) return null;
    if (c.length > 40) c = c.substring(0, 40) + '...';
    return c || null;
  }

  function getVisibleText(root) {
    if (!root || root.nodeType !== 1) return '';
    const HIDDEN_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEMPLATE', 'S', 'STRIKE', 'DEL']);
    let out = '';
    function walk(node) {
      if (!node) return;
      if (node.nodeType === 3) { out += node.nodeValue; return; }
      if (node.nodeType !== 1) return;
      if (HIDDEN_TAGS.has(node.tagName)) return;
      if (node.getAttribute && node.getAttribute('aria-hidden') === 'true') return;
      let style;
      try { style = window.getComputedStyle(node); } catch (e) { style = null; }
      if (style) {
        if (style.display === 'none') return;
        if (style.visibility === 'hidden' || style.visibility === 'collapse') return;
        const deco = style.textDecorationLine || style.textDecoration || '';
        if (deco.indexOf('line-through') !== -1) return;
      }
      const kids = node.childNodes;
      for (let i = 0; i < kids.length; i++) walk(kids[i]);
    }
    walk(root);
    return out;
  }

  function getElementText(el) {
    let raw = null;
    if (el.tagName === 'SELECT') {
      const opt = el.options[el.selectedIndex];
      raw = opt ? opt.text : null;
    } else if (el.type === 'radio' || el.type === 'checkbox') {
      const lbl = el.id && document.querySelector(`label[for="${el.id}"]`);
      const src = lbl || (el.closest && el.closest('label'));
      if (src) raw = getVisibleText(src);
    } else {
      raw = getVisibleText(el);
      if (!raw || !raw.trim()) raw = el.getAttribute && (el.getAttribute('aria-label') || el.getAttribute('title'));
    }
    return cleanText(raw);
  }

  function buildClickContext(targetEl, originalEl) {
    const href = targetEl.getAttribute ? targetEl.getAttribute('href') : null;
    const linkUrl = href && !href.startsWith('#') ? href : null;
    const productHandle = getProductHandle(linkUrl);
    const variantIdUrl = getVariantIdFromUrl(linkUrl);
    const { sectionId, sectionHandle } = getSectionInfo(targetEl);

    const ctx = {
      custom_click_name: getClickName(targetEl) || getClickName(originalEl),
      custom_button_text: getElementText(targetEl),
      custom_element_type: targetEl.tagName.toLowerCase(),
      custom_element_role: targetEl.getAttribute ? targetEl.getAttribute('role') : null,
      page_path: window.location.pathname,
      page_title: document.title
    };

    const clickId        = getClickId(targetEl) || getClickId(originalEl);
    const clickLinkName  = getClickLinkName(targetEl) || getClickLinkName(originalEl);
    const clickProduct   = getClickProduct(targetEl) || getClickProduct(originalEl);
    const clickVariant   = getClickVariant(targetEl) || getClickVariant(originalEl);
    const clickProductId = getClickProductId(targetEl) || getClickProductId(originalEl);
    const clickVariantId = getClickVariantId(targetEl) || getClickVariantId(originalEl);
    const elementId      = getElementId(targetEl);

    if (clickId != null)        ctx.custom_click_id = clickId;
    if (clickLinkName != null)  ctx.custom_click_link_name = clickLinkName;
    if (clickProduct != null)   ctx.custom_click_product = clickProduct;
    if (clickVariant != null)   ctx.custom_click_variant = clickVariant;
    if (clickProductId != null) ctx.custom_click_productid = clickProductId;
    if (clickVariantId != null) ctx.custom_click_variantid = clickVariantId;
    if (elementId != null)      ctx.custom_element_id = elementId;
    if (linkUrl != null)        ctx.custom_link_url = linkUrl;
    if (productHandle != null)  ctx.custom_product_handle = productHandle;
    if (variantIdUrl != null)   ctx.custom_variant_id = variantIdUrl;
    if (sectionId != null)      ctx.custom_section_id = sectionId;
    if (sectionHandle != null)  ctx.custom_section_handle = sectionHandle;
    return ctx;
  }

  const CLICKABLE_SELECTOR = [
    'button',
    'a',
    'input[type="submit"]',
    'input[type="radio"]',
    'input[type="checkbox"]',
    'select',
    '[role="button"]',
    '[role="link"]',
    '[role="checkbox"]',
    '[role="radio"]',
    '[role="switch"]',
    '[role="tab"]',
    '[role="menuitem"]',
    '[role="menuitemcheckbox"]',
    '[role="menuitemradio"]',
    '[role="option"]',
    '[role="treeitem"]',
    '[aria-pressed]',
    '[aria-expanded]',
    '[aria-selected]'
  ].join(', ');

  function handleClick(event) {
    const originalEl = event.target;
    const clickNameEl = originalEl.closest && originalEl.closest('[data-click-name]');
    const subscriptionWidget = originalEl.closest && originalEl.closest('.subscription-widget, .js-subscription-option');

    let target = originalEl.closest && originalEl.closest(CLICKABLE_SELECTOR);
    if (!target && clickNameEl) target = clickNameEl;
    if (!target && subscriptionWidget) {
      target = originalEl.closest('.subscription-option, .subscription-frequency, .subscription-frequency__button') || originalEl;
    }
    if (!target) return;

    const ctx = buildClickContext(target, originalEl);

    const productIdEl = getDataAttribute(target, 'product-id') || getDataAttribute(originalEl, 'product-id');
    if (productIdEl && (target.tagName === 'SELECT' || target.type === 'radio')) {
      const sv = getSelectedVariantForProduct(target, productIdEl);
      if (sv) {
        ctx.custom_click_variant = cleanText(sv.title);
        ctx.custom_click_variantid = sv.id;
      }
    }

    if (target.tagName === 'SELECT') return;

    const ga4ClickEl = originalEl.closest && originalEl.closest('[data-ga4-click]');
    if (ga4ClickEl) return fireCustomGa4Event(ga4ClickEl, ctx);

    const heroEl = originalEl.closest && originalEl.closest('[data-ga4-hero]');
    if (heroEl && (target.tagName === 'A' || target.tagName === 'BUTTON' || (target.classList && target.classList.contains('btn')))) {
      return fireHeroClick(heroEl, ctx, target);
    }

    const navAbEl = originalEl.closest && originalEl.closest('[data-ga4-nav-ab="1"]');
    if (navAbEl && (target.tagName === 'A' || target.tagName === 'BUTTON')) {
      return fireNavClick(navAbEl, ctx, target);
    }

    const atcEl = originalEl.closest && originalEl.closest('.js-add-to-cart, .js-add-to-cart-alias, .js-add-to-cart-variant');
    if (atcEl) return fireAddToCart(atcEl, ctx);

    fireElementClick(ctx);
  }

  function fireElementClick(ctx) {
    sendToGA4('element_click', ctx);
  }

  function fireCustomGa4Event(ga4ClickEl, ctx) {
    const eventName = ga4ClickEl.getAttribute('data-ga4-click');
    let declaredParams = {};
    const pAttr = ga4ClickEl.getAttribute('data-ga4-params');
    if (pAttr) {
      try { declaredParams = JSON.parse(pAttr) || {}; } catch (e) {}
    }
    const payload = Object.assign({}, ctx, declaredParams);
    sendToGA4(eventName, payload);
  }

  const __heroAbGroups = {};

  function removeHeroAbCandidate(heroEl) {
    const slideEl = heroEl.closest && heroEl.closest('.swiper-slide');
    if (slideEl && slideEl.contains(heroEl)) {
      slideEl.remove();
      return;
    }
    heroEl.remove();
  }

  function chooseHeroAbIndex(count) {
    if (count <= 1) return 0;

    if (window.crypto && window.crypto.getRandomValues) {
      const values = new Uint32Array(1);
      const max = Math.floor(0x100000000 / count) * count;
      do {
        window.crypto.getRandomValues(values);
      } while (values[0] >= max);
      return values[0] % count;
    }

    return Math.floor(Math.random() * count);
  }

  function fireHeroClick(heroEl, ctx, clickedTarget) {
    const heroTitle = heroEl.getAttribute('data-ga4-hero-title') || '';
    const abEnabled = heroEl.getAttribute('data-ga4-hero-ab') === '1';
    const abVariant = heroEl.getAttribute('data-ga4-hero-variant') || '';
    const params = Object.assign({}, ctx, {
      hero_title: heroTitle
    });
    if (abEnabled && abVariant) params.hero_variant = abVariant;
    if (clickedTarget && clickedTarget.href) params.custom_link_url = clickedTarget.href;
    sendToGA4('hero_click', params);
  }

  function fireHeroViewed(heroEl) {
    const heroTitle = heroEl.getAttribute('data-ga4-hero-title') || '';
    const abEnabled = heroEl.getAttribute('data-ga4-hero-ab') === '1';
    const abVariant = heroEl.getAttribute('data-ga4-hero-variant') || '';
    const params = {
      page_path: window.location.pathname,
      page_title: document.title,
      hero_title: heroTitle
    };
    if (abEnabled && abVariant) params.hero_variant = abVariant;
    sendToGA4('hero_viewed', params);
  }

  function initHeroTracking() {
    const heroes = document.querySelectorAll('[data-ga4-hero="1"]');
    heroes.forEach(function (heroEl) {
      const heroTitle = heroEl.getAttribute('data-ga4-hero-title') || '';
      const abEnabled = heroEl.getAttribute('data-ga4-hero-ab') === '1';
      const abVariant = heroEl.getAttribute('data-ga4-hero-variant') || '';
      const designMode = heroEl.getAttribute('data-ga4-design-mode') === '1';

      if (!abEnabled || !heroTitle || designMode) {
        queueHeroViewed(heroEl);
        return;
      }

      const cookieKey = 'ab_hero_' + heroTitle;
      const m = document.cookie.match(new RegExp('(?:^|; )' + cookieKey + '=([^;]*)'));
      const cookieVariant = m ? decodeURIComponent(m[1]) : '';

      if (!__heroAbGroups[heroTitle]) __heroAbGroups[heroTitle] = { blocks: [], timer: null };
      const group = __heroAbGroups[heroTitle];
      group.blocks.push({ el: heroEl, variant: abVariant, cookieVariant: cookieVariant, cookieKey: cookieKey });

      clearTimeout(group.timer);
      group.timer = setTimeout(function () {
        let chosen = -1;
        const latestMatch = document.cookie.match(new RegExp('(?:^|; )' + cookieKey + '=([^;]*)'));
        const storedVariant = latestMatch
          ? decodeURIComponent(latestMatch[1])
          : (group.blocks[0] && group.blocks[0].cookieVariant);
        if (storedVariant) {
          chosen = group.blocks.findIndex(function (block) {
            return block.variant === storedVariant;
          });
        }
        if (chosen < 0) {
          chosen = chooseHeroAbIndex(group.blocks.length);
          document.cookie = cookieKey + '=' + encodeURIComponent(group.blocks[chosen].variant) +
                            '; path=/; max-age=' + (60 * 60 * 24 * 30);
        }

        for (let i = 0; i < group.blocks.length; i++) {
          if (i === chosen) queueHeroViewed(group.blocks[i].el);
          else removeHeroAbCandidate(group.blocks[i].el);
        }
      }, 0);
    });
  }

  function queueHeroViewed(heroEl) {
    if (document.readyState === 'complete') {
      fireHeroViewed(heroEl);
    } else {
      window.addEventListener('load', function () { fireHeroViewed(heroEl); }, { once: true });
    }
  }

  // Nav A/B runtime swaps links before init and queues viewed records here.
  function flushNavViewedQueue() {
    var queue = window.__navAbViewedQueue;
    if (!queue || !queue.length) return;
    while (queue.length) {
      var rec = queue.shift();
      sendToGA4('nav_viewed', {
        nav_title: rec.nav_title,
        nav_variant: rec.nav_variant,
        match_count: rec.match_count,
        page_path: window.location.pathname,
        page_title: document.title
      });
    }
  }

  function fireNavClick(navEl, ctx, clickedTarget) {
    var navTitle = navEl.getAttribute('data-ga4-nav-title') || '';
    var navVariant = navEl.getAttribute('data-ga4-nav-variant') || '';
    var params = Object.assign({}, ctx, {
      nav_title: navTitle,
      nav_variant: navVariant
    });
    if (clickedTarget && clickedTarget.href) params.custom_link_url = clickedTarget.href;
    sendToGA4('nav_click', params);
  }

  function fireAddToCart(atcEl, ctx) {
    const wrapper = atcEl.closest('.js-product-wrapper') || atcEl;
    const productTitle = wrapper.getAttribute('data-title') || ctx.custom_click_product || '';
    const productVariantId = wrapper.getAttribute('data-variant') || ctx.custom_click_variantid || '';
    const productPrice = wrapper.getAttribute('data-price') || '';
    const productQty = 1;

    sendToGA4('addToCart', Object.assign({}, ctx, {
      productTitle: productTitle,
      productVariantId: productVariantId,
      productPrice: productPrice,
      productQty: productQty
    }));
  }

  function observeDataLayer() {
    // no-op
  }

  function handleUpsellAdded(event) {
    const d = event.detail || {};
    sendToGA4('upsell_add_to_cart', {
      custom_click_name: 'Cart - Upsell Added',
      custom_click_product: d.productTitle || '',
      custom_click_productid: d.productId || '',
      custom_click_variant: d.variantTitle || '',
      custom_click_variantid: d.variantId || '',
      custom_element_type: 'upsell',
      page_path: window.location.pathname,
      page_title: document.title
    });
  }

  function handleCrossSellAdded(event) {
    const d = event.detail || {};
    sendToGA4('cross_sell_add_to_cart', {
      custom_click_name: 'PDP - Cross Sell Added',
      custom_click_product: d.productTitle || '',
      custom_click_productid: d.productId || '',
      custom_click_variant: d.variantTitle || '',
      custom_click_variantid: d.variantId || '',
      custom_element_type: 'cross-sell',
      custom_source: d.source || 'PDP Cross Sell',
      page_path: window.location.pathname,
      page_title: document.title
    });
  }

  let searchOpenDispatched = false;

  function dispatchSearchOpen(trigger) {
    if (searchOpenDispatched) return;
    searchOpenDispatched = true;
    document.dispatchEvent(new CustomEvent('search:open', { detail: { trigger: trigger || 'unknown' } }));
  }

  function dispatchSearchClose(term) {
    if (!searchOpenDispatched) return;
    searchOpenDispatched = false;
    document.dispatchEvent(new CustomEvent('search:close', { detail: { searchTerm: term || '' } }));
  }

  function wireSearchAutoDetection() {
    function hookSearchDetails() {
      document.querySelectorAll('details').forEach(function (det) {
        if (det.dataset.ga4SearchHooked === '1') return;
        if (!det.querySelector('input[type="search"], input[name="q"]')) return;
        det.dataset.ga4SearchHooked = '1';
        det.addEventListener('toggle', function () {
          if (det.open) {
            dispatchSearchOpen('header-icon');
          } else {
            const input = det.querySelector('input[type="search"], input[name="q"]');
            dispatchSearchClose(input && input.value ? input.value.trim() : '');
          }
        });
      });
    }
    hookSearchDetails();
    setTimeout(hookSearchDetails, 500);
    setTimeout(hookSearchDetails, 2000);

    document.addEventListener('click', function (event) {
      const target = event.target && event.target.closest && event.target.closest('[data-click-name]');
      if (!target) return;
      const name = target.getAttribute('data-click-name') || '';
      if (/search.*(toggle|open)/i.test(name)) {
        dispatchSearchOpen(name);
      } else if (/search.*close/i.test(name)) {
        const input = document.querySelector('input[type="search"], input[name="q"]');
        dispatchSearchClose(input && input.value ? input.value.trim() : '');
      }
    }, true);

    document.addEventListener('focusin', function (event) {
      const t = event.target;
      if (!t || t.tagName !== 'INPUT') return;
      if (t.type === 'search' || t.getAttribute('name') === 'q') dispatchSearchOpen('input-focus');
    }, true);

    document.addEventListener('submit', function (event) {
      const form = event.target;
      if (!form || form.tagName !== 'FORM') return;
      const action = form.getAttribute('action') || '';
      if (!action.includes('/search')) return;
      const input = form.querySelector('input[type="search"], input[name="q"]');
      const term = input && input.value ? input.value.trim() : '';
      if (term) document.dispatchEvent(new CustomEvent('search:query', { detail: { searchTerm: term } }));
    }, true);
  }

  function handleSearchOpen(event) {
    const d = event.detail || {};
    sendToGA4('search_open', {
      custom_search_trigger: d.trigger || 'unknown',
      page_path: window.location.pathname,
      page_title: document.title
    });
  }

  function handleSearchClose(event) {
    const d = event.detail || {};
    sendToGA4('search_close', {
      custom_search_term: d.searchTerm || '',
      custom_had_results: d.hadResults ? 'true' : 'false',
      custom_product_count: d.productCount || 0,
      custom_article_count: d.articleCount || 0,
      page_path: window.location.pathname,
      page_title: document.title
    });
  }

  function handleSearchQuery(event) {
    const d = event.detail || {};
    const term = d.searchTerm || '';
    sendToGA4('search_query', {
      custom_search_term: term,
      custom_search_term_length: term.length,
      page_path: window.location.pathname,
      page_title: document.title
    });
  }

  function handleSearchResults(event) {
    const d = event.detail || {};
    const productCount = d.productCount || 0;
    const articleCount = d.articleCount || 0;
    const total = productCount + articleCount;
    sendToGA4('search_results_loaded', {
      custom_search_term: d.searchTerm || '',
      custom_product_count: productCount,
      custom_article_count: articleCount,
      custom_total_results: total,
      custom_has_results: total > 0 ? 'true' : 'false',
      page_path: window.location.pathname,
      page_title: document.title
    });
    if (total === 0 && d.searchTerm) {
      sendToGA4('search_no_results', {
        custom_search_term: d.searchTerm,
        custom_search_term_length: d.searchTerm.length,
        page_path: window.location.pathname,
        page_title: document.title
      });
    }
  }

  function handleSearchResultClick(event) {
    const d = event.detail || {};
    const p = {
      custom_search_term: d.searchTerm || '',
      custom_result_type: d.resultType || 'unknown',
      custom_result_title: d.resultTitle || '',
      custom_result_position: d.resultPosition || 0,
      custom_result_id: d.resultId || '',
      custom_result_url: d.resultUrl || '',
      page_path: window.location.pathname,
      page_title: document.title
    };
    if (d.resultVariant) p.custom_result_variant = d.resultVariant;
    if (d.resultVariantId) p.custom_result_variant_id = d.resultVariantId;
    sendToGA4('search_result_click', p);
  }

  function getSelectedVariantForProduct(el, productId) {
    if (!productId) return null;
    const script = document.querySelector(`script[data-product-variants="${productId}"]`);
    if (!script) return null;
    let data;
    try { data = JSON.parse(script.textContent); } catch (e) { return null; }

    const container = el.closest('.pdp-cross-sell__variant-dropdowns, [data-variant-dropdowns]');
    if (!container) return null;

    const opts = [];
    container.querySelectorAll('.pdp-cross-sell__swatch-group, [data-swatch-group]').forEach((g) => {
      const i = parseInt(g.getAttribute('data-option-index'));
      const checked = g.querySelector('input:checked');
      if (checked && !isNaN(i)) opts[i] = checked.value;
    });
    container.querySelectorAll('select[data-option-index]').forEach((s) => {
      const i = parseInt(s.getAttribute('data-option-index'));
      if (!isNaN(i)) opts[i] = s.value;
    });

    return data.find((v) => (
      (!opts[0] || v.option1 === opts[0]) &&
      (!opts[1] || v.option2 === opts[1]) &&
      (!opts[2] || v.option3 === opts[2])
    )) || null;
  }

  function handleChange(event) {
    const el = event.target;

    if (el.tagName === 'SELECT') {
      const ctx = buildClickContext(el, el);

      const opt = el.options[el.selectedIndex];
      if (opt) {
        const pt = opt.getAttribute('data-product-title');
        const pi = opt.getAttribute('data-product-id');
        const vt = opt.getAttribute('data-variant-title') || opt.getAttribute('data-display-label');
        const vi = opt.getAttribute('data-variant-id');
        if (pt) ctx.custom_click_product = pt;
        if (pi) ctx.custom_click_productid = pi;
        if (vt) ctx.custom_click_variant = cleanText(vt);
        if (vi) ctx.custom_click_variantid = vi;
      }

      const productId = getDataAttribute(el, 'product-id');
      if (productId) {
        const sv = getSelectedVariantForProduct(el, productId);
        if (sv) {
          ctx.custom_click_variant = cleanText(sv.title);
          ctx.custom_click_variantid = sv.id;
        }
      }

      if (!ctx.custom_button_text) ctx.custom_button_text = getElementText(el);
      sendToGA4('element_click', ctx);
      return;
    }

    if (el.type === 'radio' || el.type === 'checkbox') {
      handleClick(event);
    }
  }

  const LINK_FLUSH_READY_MS = 80;
  const LINK_FLUSH_MAX_MS = 350;

  function handleLinkNavigation(event) {
    if (event.defaultPrevented) return;
    if (event.button !== 0) return;
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;

    const a = event.target && event.target.closest && event.target.closest('a[href]');
    if (!a) return;
    if (a.hasAttribute('download')) return;
    if (a.target && a.target !== '' && a.target !== '_self') return;

    const href = a.getAttribute('href');
    if (!href) return;
    if (href.startsWith('#')) return;
    if (href.startsWith('javascript:')) return;
    if (href.startsWith('mailto:')) return;
    if (href.startsWith('tel:')) return;

    if (!isAnalyticsAllowed()) return;

    event.preventDefault();

    const destination = a.href;
    const start = Date.now();

    function go() { window.location.href = destination; }

    function waitForFlush() {
      const elapsed = Date.now() - start;
      const gtagReady = typeof window.gtag === 'function' && window.gtag !== window.ga4Event;
      if (gtagReady || elapsed >= LINK_FLUSH_MAX_MS) {
        setTimeout(go, LINK_FLUSH_READY_MS);
      } else {
        setTimeout(waitForFlush, 20);
      }
    }

    waitForFlush();
  }

  function init() {
    observeDataLayer();
    initHeroTracking();
    flushNavViewedQueue();
    wireSearchAutoDetection();

    document.addEventListener('click', handleClick, true);
    document.addEventListener('click', handleLinkNavigation, false);
    document.addEventListener('change', handleChange, true);

    document.addEventListener('upsell:added', handleUpsellAdded, true);
    document.addEventListener('cross-sell:added', handleCrossSellAdded, true);

    document.addEventListener('search:open', handleSearchOpen, true);
    document.addEventListener('search:close', handleSearchClose, true);
    document.addEventListener('search:query', handleSearchQuery, true);
    document.addEventListener('search:results', handleSearchResults, true);
    document.addEventListener('search:result_click', handleSearchResultClick, true);

    if (DEBUG) console.log('[GA4] google-events.js initialized (piggybacking on window.dataLayer; G&Y app owns gtag config)');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
